// Curaprox PDP info blocks — accordion animation/exclusivity + description read-more.
// Two self-contained custom elements, no dependencies. Native <details> still works if JS fails.

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const DURATION = 280;

function clearInline(el) {
  el.style.height = '';
  el.style.overflow = '';
  el.style.paddingTop = '';
  el.style.paddingBottom = '';
}

/**
 * Smoothly animate an element's height between two pixel values.
 *
 * - Uses `fill: both` and commits the real resting state *before* releasing the
 *   fill, so the element never snaps back to its natural height for a frame at the
 *   end (that snap caused the "double layout shift" when closing a panel).
 * - When `collapsePadding` is set, the element's vertical padding is animated to 0
 *   on whichever side has height 0, so a padded panel can fully collapse instead
 *   of flooring at its padding height (border-box can't shrink below its padding).
 *
 * @param {HTMLElement} el
 * @param {number} fromH            start height in px
 * @param {number} toH              end height in px
 * @param {boolean} collapsePadding animate vertical padding with the height
 * @param {Function} [applyEnd]     set the resting state at the end (e.g. details.open = false)
 */
function slide(el, fromH, toH, collapsePadding, applyEnd) {
  if (el.curaAnim) {
    el.curaAnim.cancel();
    el.curaAnim = null;
  }

  // Reduced motion (or no Web Animations support): jump straight to the end state.
  if (reducedMotion.matches || typeof el.animate !== 'function') {
    if (applyEnd) applyEnd();
    clearInline(el);
    return;
  }

  const cs = getComputedStyle(el);
  const padTop = cs.paddingTop;
  const padBottom = cs.paddingBottom;
  const from = { height: `${fromH}px` };
  const to = { height: `${toH}px` };

  if (collapsePadding) {
    from.paddingTop = fromH === 0 ? '0px' : padTop;
    from.paddingBottom = fromH === 0 ? '0px' : padBottom;
    to.paddingTop = toH === 0 ? '0px' : padTop;
    to.paddingBottom = toH === 0 ? '0px' : padBottom;
  }

  el.style.overflow = 'hidden';
  const animation = el.animate([from, to], { duration: DURATION, easing: 'ease', fill: 'both' });
  el.curaAnim = animation;

  animation.onfinish = () => {
    el.curaAnim = null;
    if (applyEnd) applyEnd(); // commit resting state while the fill still holds the end values
    clearInline(el);
    animation.cancel(); // release the fill — element now sits at its natural/clamped height
  };
}

/**
 * <cura-accordion> wraps a set of <details> rows.
 * - Smoothly animates open/close (flash-free, collapses to a true 0 height).
 * - data-exclusive="true" (default): opening one row closes the others in the group,
 *   in sync with the opening animation, so the page height changes only once.
 */
class CuraAccordion extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.exclusive = this.getAttribute('data-exclusive') !== 'false';

    this.rows().forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (summary) {
        summary.addEventListener('click', (event) => this.onClick(event, details));
      }
    });
  }

  rows() {
    return Array.from(this.querySelectorAll(':scope > details'));
  }

  panel(details) {
    return details.querySelector(':scope > summary ~ *');
  }

  onClick(event, details) {
    event.preventDefault();

    if (details.open) {
      this.collapse(details);
      return;
    }

    if (this.exclusive) {
      const summary = event.currentTarget;
      let collapsedAbove = false;
      this.rows().forEach((other) => {
        if (other === details || !other.open) return;
        if (other.compareDocumentPosition(details) & Node.DOCUMENT_POSITION_FOLLOWING) collapsedAbove = true;
        this.collapse(other);
      });
      // A collapsing row ABOVE the clicked one pulls the clicked summary up by its
      // height (a long Specifications list moves Shipping clear off the top of the
      // viewport). Keep the clicked summary where the shopper clicked it.
      if (collapsedAbove) this.pin(summary);
    }
    this.expand(details);
  }

  /**
   * Hold `el` at its current viewport position while the sibling collapse/expand
   * animations run, by counter-scrolling the window each frame by however far the
   * element drifted. Measures real positions, so it composes with browser scroll
   * anchoring and with the reduced-motion (instant) path.
   */
  pin(el) {
    const targetTop = el.getBoundingClientRect().top;
    const started = performance.now();
    const tick = () => {
      const drift = el.getBoundingClientRect().top - targetTop;
      // 'instant': the theme sets scroll-behavior: smooth, which would animate each correction.
      if (Math.abs(drift) > 0.5) window.scrollBy({ top: drift, behavior: 'instant' });
      if (performance.now() - started < DURATION + 100) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  expand(details) {
    const panel = this.panel(details);
    if (!panel) {
      details.open = true;
      return;
    }
    details.open = true;
    slide(panel, 0, panel.scrollHeight, true, null);
  }

  collapse(details) {
    const panel = this.panel(details);
    if (!panel) {
      details.open = false;
      return;
    }
    const start = panel.curaAnim ? panel.getBoundingClientRect().height : panel.scrollHeight;
    slide(panel, start, 0, true, () => {
      details.open = false;
    });
  }
}

/**
 * <cura-readmore> clamps its content and smoothly toggles a Read more / Read less button.
 * The button is hidden automatically when the content is short enough to fit.
 */
class CuraReadMore extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.content = this.querySelector('[data-cura-readmore-content]');
    this.button = this.querySelector('[data-cura-readmore-toggle]');
    this.label = this.querySelector('[data-cura-readmore-label]');
    if (!this.content || !this.button) return;

    this.moreLabel = this.getAttribute('data-more-label') || 'Read more';
    this.lessLabel = this.getAttribute('data-less-label') || 'Read less';

    this.isOpen = this.hasAttribute('data-expanded');
    this.setLabel(this.isOpen);
    this.button.addEventListener('click', () => this.toggle());

    this.refresh();
    window.addEventListener('resize', () => this.refresh(), { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.refresh());
    }

    // Survive morphs. When this lives inside the variant picker (e.g. the toothbrush colour
    // swatches), selecting a variant morphs the picker with fresh server HTML that never
    // carries `data-expanded` — so the morph strips the attribute and the grid snaps shut,
    // even though the element instance (and `this.isOpen`) live on. Re-assert the expanded
    // resting state whenever the attribute is removed by anything other than our own
    // collapse() (which sets `isOpen = false` before touching the attribute). The observer
    // runs as a microtask after the morph but before paint, so there's no visible flash.
    this.expandedObserver = new MutationObserver(() => {
      if (this.isOpen && !this.hasAttribute('data-expanded')) {
        this.setAttribute('data-expanded', '');
        this.setLabel(true);
      }
    });
    this.expandedObserver.observe(this, { attributes: true, attributeFilter: ['data-expanded'] });
  }

  // Show the toggle only when the (collapsed) content actually overflows.
  refresh() {
    if (this.hasAttribute('data-expanded')) return;
    const overflowing = this.content.scrollHeight - this.content.clientHeight > 1;
    this.button.hidden = !overflowing;
  }

  toggle() {
    if (this.hasAttribute('data-expanded')) this.collapse();
    else this.expand();
  }

  expand() {
    this.isOpen = true;
    const from = this.content.clientHeight; // clamped height
    this.setAttribute('data-expanded', ''); // remove the clamp
    const to = this.content.scrollHeight; // full height
    this.setLabel(true);
    slide(this.content, from, to, false, null);
  }

  collapse() {
    this.isOpen = false;
    const from = this.content.scrollHeight; // currently expanded (full)
    this.removeAttribute('data-expanded'); // apply clamp to measure target
    const to = this.content.clientHeight; // clamped height
    this.setAttribute('data-expanded', ''); // remove clamp again so we animate from full
    this.setLabel(false);
    slide(this.content, from, to, false, () => {
      this.removeAttribute('data-expanded'); // resting state: clamp applied
    });
  }

  setLabel(expanded) {
    this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (this.label) this.label.textContent = expanded ? this.lessLabel : this.moreLabel;
  }
}

if (!customElements.get('cura-accordion')) {
  customElements.define('cura-accordion', CuraAccordion);
}
if (!customElements.get('cura-readmore')) {
  customElements.define('cura-readmore', CuraReadMore);
}
